'use client';

import { useId, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { RiLockLine, RiMailLine, RiUserLine } from '@remixicon/react';
import { useLocale, useTranslations } from 'next-intl';

import {
  AuthBrand,
  AuthDivider,
  AuthField,
  FieldError,
  FormAlert,
  GoogleButton,
  MIN_PASSWORD,
  PasswordReveal,
  PasswordStrength,
  SubmitButton,
  errorV,
  fieldV,
  legalChunks,
} from '@/components/auth-parts';
import { ApiError } from '@/lib/api';
import { Link, useRouter } from '@/i18n/navigation';
import { DASH_HOME } from '@/lib/dash-home';
import { sceneContainer, sceneItem } from '@/lib/motion';
import { signUp } from '@/lib/session';
import { siteLinks } from '@/lib/site-links';

/**
 * Kayıt formu — bölünmüş tuvalin beyaz yarısı.
 *
 * Giriş formunun kardeşi; alan kutusu, göz düğmesi, gönder butonu ve hata
 * bölgeleri ortak (`auth-parts.tsx`). Buradaki fark üç yerde:
 *
 * 1. **Üç alan var, iki değil.** Ad, e-posta, şifre. Daha fazlası (şirket adı,
 *    telefon, çalışma alanı) kayıt anında sorulmuyor — hesap açtıktan sonra
 *    sorulabilecek her şey kayıt ekranından çıkarıldı. Kayıt ekranı bir form
 *    değil bir eşik; ne kadar kısaysa o kadar iyi.
 *
 * 2. **Şifre gücü ölçeri.** Kutunun altında dört bölme + tek satır metin
 *    (`PasswordStrength`, auth-parts.tsx — şifre sıfırlama ekranıyla ortak).
 *
 * 3. **"Beni hatırla" yok.** Yeni hesapta oturum zaten açılacak; kutu sormak
 *    kullanıcıya cevabı olmayan bir soru sormak olurdu.
 *
 * ── Hareket neyi açıklıyor ────────────────────────────────────────────────
 *
 * Sahne girişi giriş ekranıyla aynı sırayı izliyor (yukarıdan aşağı, formun
 * okunma sırası). Yeni olan tek hareket ölçerin kendisi: kullanıcı yazdıkça
 * bölmeler SOLDAN SAĞA doluyor. Yön önemli — dolan bir ölçek "arttı" der,
 * yanıp sönen bir renk yalnızca "değişti" der.
 *
 * ── Backend ───────────────────────────────────────────────────────────────
 *
 * Auth uçları henüz yok (CLAUDE.md → Durum). Gönderim istemci doğrulamasını
 * çalıştırıp bekleme durumunu gösteriyor, sonra "servis bağlı değil" satırını
 * basıyor — giriş formuyla aynı davranış. Uçlar gelince taklit gecikme
 * `api.post('/auth/register', …)` ile değiştirilecek.
 */

/** Kabaca "@ var, nokta var, boşluk yok". Asıl doğrulama sunucuda. */
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Errors = { name?: string; email?: string; password?: string; form?: string };

export function RegisterForm() {
  const t = useTranslations('register');
  const uid = useId();
  const router = useRouter();
  /* Kayıt anındaki arayüz dili kullanıcıya yazılıyor: doğrulama e-postası ve
     sonraki oturumlar bu dilde geliyor. Tarayıcı dilini tahmin etmek yerine
     kullanıcının o an baktığı sayfanın dilini almak daha doğru bir sinyal. */
  const locale = useLocale();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [reveal, setReveal] = useState(false);
  const [pending, setPending] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  /* İlk denemeden ÖNCE alan alan uyarı basmak, kullanıcı daha adını yazarken
     "geçersiz" demek olur. Denemeden sonra anlık doğrulama açılıyor. */
  const [tried, setTried] = useState(false);

  const nameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  function validate(next = { name, email, password }): Errors {
    const found: Errors = {};
    if (next.name.trim().length < 2) found.name = t('name.error');
    if (!EMAIL.test(next.email.trim())) found.email = t('email.error');
    if (next.password.length < MIN_PASSWORD) found.password = t('password.error');
    return found;
  }

  function onChange(field: 'name' | 'email' | 'password', value: string) {
    const next = { name, email, password, [field]: value };
    if (field === 'name') setName(value);
    else if (field === 'email') setEmail(value);
    else setPassword(value);
    if (tried) setErrors(validate(next));
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setTried(true);

    const found = validate();
    if (Object.keys(found).length > 0) {
      setErrors(found);
      /* Odak İLK hatalı alana gider — yoksa klavye kullanıcısı, ekranın
         yukarısında beliren uyarıyı ancak geri gezinerek buluyor. */
      (found.name ? nameRef : found.email ? emailRef : passwordRef).current?.focus();
      return;
    }

    setErrors({});
    setPending(true);

    try {
      await signUp({
        name: name.trim(),
        email: email.trim().toLowerCase(),
        password,
        locale: locale === 'tr' ? 'tr' : 'en',
      });
      // Kayıt oturumu da açıyor (API çerezleri yazdı). Kullanıcıyı doğrulama
      // ekranında bekletmiyoruz — panele girip ürünü görebilmeli; doğrulama
      // uyarısı panelin üstünde kalıcı bir şerit olarak duruyor.
      router.push(DASH_HOME);
    } catch (error) {
      setPending(false);

      if (error instanceof ApiError && error.status === 409) {
        // Kayıt formunda dürüst olmak zorundayız: "bu e-posta kullanımda"
        // demezsek kullanıcı neden ilerleyemediğini anlayamaz.
        setErrors({ email: t('email.taken') });
        emailRef.current?.focus();
        return;
      }

      if (error instanceof ApiError && error.status === 429) {
        setErrors({ form: t('tooMany') });
        return;
      }

      setErrors({ form: t('notConnected') });
    }
  }

  const nameId = `${uid}-name`;
  const emailId = `${uid}-email`;
  const passwordId = `${uid}-password`;
  const strengthId = `${uid}-strength`;

  return (
    <section className="auth-col">
      <motion.div
        className="auth-form-inner"
        variants={sceneContainer}
        initial="hidden"
        animate="show"
      >
        <motion.div variants={sceneItem}>
          <AuthBrand />
        </motion.div>

        <motion.div className="auth-head" variants={sceneItem}>
          <h1 className="auth-title">{t('title')}</h1>
          <p className="auth-lead">{t('lead')}</p>
        </motion.div>

        <motion.div variants={sceneItem}>
          <GoogleButton label={t('google')} />
        </motion.div>

        <motion.div variants={sceneItem}>
          <AuthDivider label={t('divider')} />
        </motion.div>

        <form className="auth-form" onSubmit={onSubmit} noValidate>
          {/* İlk alan: üstünde konumu değişen bir şey yok, `layout` gerekmiyor —
              o yüzden tam `sceneItem` (y + blur) ile girebiliyor. */}
          <motion.div className="auth-field" variants={sceneItem}>
            <label className="auth-label" htmlFor={nameId}>
              {t('name.label')}
            </label>
            <AuthField
              invalid={Boolean(errors.name)}
              icon={<RiUserLine size={18} aria-hidden="true" />}
            >
              <input
                ref={nameRef}
                id={nameId}
                className="auth-input"
                type="text"
                name="name"
                autoComplete="name"
                placeholder={t('name.placeholder')}
                value={name}
                onChange={(e) => onChange('name', e.target.value)}
                aria-invalid={Boolean(errors.name)}
                aria-describedby={errors.name ? `${nameId}-error` : undefined}
              />
            </AuthField>
            <FieldError id={`${nameId}-error`} message={errors.name} />
          </motion.div>

          {/* Buradan aşağısı `layout="position"`: üstteki hata belirince
              KAYARAK yer değiştiriyorlar. "position" bilinçli — `layout` tek
              başına kutunun BOYUTUNU da ölçekler ve içerideki metin gerilir. */}
          <motion.div className="auth-field" variants={fieldV} layout="position">
            <label className="auth-label" htmlFor={emailId}>
              {t('email.label')}
            </label>
            <AuthField
              invalid={Boolean(errors.email)}
              icon={<RiMailLine size={18} aria-hidden="true" />}
            >
              <input
                ref={emailRef}
                id={emailId}
                className="auth-input"
                type="email"
                name="email"
                autoComplete="email"
                inputMode="email"
                placeholder={t('email.placeholder')}
                value={email}
                onChange={(e) => onChange('email', e.target.value)}
                aria-invalid={Boolean(errors.email)}
                aria-describedby={errors.email ? `${emailId}-error` : undefined}
              />
            </AuthField>
            <FieldError id={`${emailId}-error`} message={errors.email} />
          </motion.div>

          <motion.div className="auth-field" variants={fieldV} layout="position">
            <label className="auth-label" htmlFor={passwordId}>
              {t('password.label')}
            </label>
            <AuthField
              invalid={Boolean(errors.password)}
              icon={<RiLockLine size={18} aria-hidden="true" />}
            >
              <input
                ref={passwordRef}
                id={passwordId}
                className="auth-input"
                type={reveal ? 'text' : 'password'}
                name="password"
                autoComplete="new-password"
                placeholder={t('password.placeholder')}
                value={password}
                onChange={(e) => onChange('password', e.target.value)}
                aria-invalid={Boolean(errors.password)}
                /* Ölçer canlı bölge DEĞİL: her tuş vuruşunda konuşan bir
                   uyarı, şifre yazarken duyulacak en kötü şey. Alana bağlı
                   açıklama olarak duruyor — ekran okuyucu isteyince okuyor. */
                aria-describedby={
                  errors.password ? `${passwordId}-error` : password ? strengthId : undefined
                }
              />
              <PasswordReveal
                revealed={reveal}
                onToggle={() => setReveal((v) => !v)}
                showLabel={t('password.show')}
                hideLabel={t('password.hide')}
              />
            </AuthField>

            {/* Hata varken ölçer YOK. İkisi aynı anda durunca ("Şifre en az 8
                karakter olmalı." + "Zayıf · en az 8 karakter") aynı şey iki kez
                söylenmiş oluyor ve kırmızı satır alana uzak düşüyor. Uyarı
                kutunun hemen altında, ölçer onun yerine geçiyor. */}
            <FieldError id={`${passwordId}-error`} message={errors.password} />

            <AnimatePresence initial={false} mode="popLayout">
              {!errors.password && (
                <motion.div
                  key="strength"
                  variants={errorV}
                  initial="hidden"
                  animate="show"
                  exit="exit"
                >
                  <PasswordStrength id={strengthId} value={password} />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          <FormAlert message={errors.form} />

          <motion.div variants={fieldV} layout="position">
            <SubmitButton pending={pending} label={t('submit')} pendingLabel={t('submitting')} />
          </motion.div>
        </form>

        <motion.p className="auth-alt" variants={sceneItem}>
          {t('haveAccount')}{' '}
          <Link href={siteLinks.login.href} className="auth-alt-link">
            {t('login')}
          </Link>
        </motion.p>

        <motion.p className="auth-legal" variants={sceneItem}>
          {t.rich('legal', legalChunks)}
        </motion.p>
      </motion.div>
    </section>
  );
}
